import { cloneDeep } from "lodash";
import { Session } from "@models/ISchema";
import { InitialStateType } from "../context/EditSessionsContext";
import {
  sessionsHaveTheSameBlocks,
  sessionsHaveTheSameInstructors,
  sessionsHaveTheSameClassrooms,
  sessionsHaveTheSameCheckedIntervals,
  sessionsHaveTheSameDay,
} from "./compareSelected";
import { Assignment, EditedSession } from "../context/formData.reducer";

/**
 * Given an array of `sessions` and an object with the edited resources,
 * return an `Assignment` object where each value it's true by default if the
 * assignment hasn't been edited, otherwise, a resource it's true only if all
 * the given sessions have the same assignment on the specific resource.
 *
 * @param sessions - EditedSession[]
 * @param assignments - Assignment
 *
 * @returns Assignment
 */
const sessionsHaveTheSameEditedResources = (
  sessions: EditedSession[] = null,
  assignments: Assignment,
): Assignment => {
  return {
    blocks: assignments?.blocks ? sessionsHaveTheSameBlocks(sessions) : true,
    day: assignments?.day ? sessionsHaveTheSameDay(sessions) : true,
    instructors: assignments?.instructors ? sessionsHaveTheSameInstructors(sessions) : true,
    classrooms: assignments?.classrooms ? sessionsHaveTheSameClassrooms(sessions) : true,
    intervals: assignments?.intervals ? sessionsHaveTheSameCheckedIntervals(sessions) : true,
  };
};

export const compare = (newSession: Session | EditedSession, state: InitialStateType): boolean => {
  const eqById = (a: Session | EditedSession, b: Session | EditedSession) => a?.id === b?.id;

  const {
    selectedSessions: initialSelectedSessions,
    savedSessions: initialSavedSessions,
    assignmentEdited,
    sessionsToCreate,
    editedSessions,
  } = state?.form;

  const selectedSessions = initialSelectedSessions ? cloneDeep(initialSelectedSessions) : [];
  const savedSessions = initialSavedSessions ? cloneDeep(initialSavedSessions) : [];

  // Busca si ya estaba seleccionada esta sesión
  const clickedSession = selectedSessions.find(session => eqById(newSession, session));
  const savedSession =
    savedSessions.find(session => eqById(newSession, session)) ??
    sessionsToCreate.find(session => eqById(newSession, session));

  /** Si la sesión NO está guardada y hay cambios */
  if (clickedSession && !savedSession) {
    const selected = clickedSession;
    const equalityByResource = sessionsHaveTheSameEditedResources(
      [selected, editedSessions],
      assignmentEdited,
    );
    return Object.values(equalityByResource).every(value => value === true);
  }

  // Si alguna sección del formulario está habilitada para editar
  if (Object.values(assignmentEdited ?? {}).some(value => value === true)) {
    /** Si la sesión está guardada y hay cambios */
    if (clickedSession && savedSession) {
      const equalityByResource = sessionsHaveTheSameEditedResources(
        [savedSession, editedSessions],
        assignmentEdited,
      );
      return Object.values(equalityByResource).every(value => value === true);
    }
    // Si está agregando una sesión nueva - validar las otras sesiones
    if (!clickedSession) {
      const savedSessionsById = savedSessions.reduce((acc, session) => {
        acc[session.id] = session;
        return acc;
      }, {} as Record<string, Session | EditedSession>);
      let selectedReturn = true;
      for (const clone of selectedSessions) {
        const selected = savedSessionsById[clone.id] ?? clone;
        const equalityByResource = sessionsHaveTheSameEditedResources(
          [selected, editedSessions],
          assignmentEdited,
        );
        if (!Object.values(equalityByResource).every(value => value === true)) {
          selectedReturn = false;
          break;
        }
      }
      return selectedReturn;
    }
  }

  return true;
};
